import React, { useMemo } from 'react'
import { useFormState } from 'react-final-form';
import { Datagrid, ListContextProvider, TextField, useTranslate } from 'react-admin';
import { Grid, makeStyles, Paper, Typography } from '@material-ui/core';
import { CheckCircleOutline, ErrorOutline, InfoOutlined } from '@material-ui/icons';

import { ListView } from '../../../../common/cards/ListCard';
import { useList } from '../../../../common/ra/useList';
import TimeRangeField from '../../../../common/fields/TimeRangeField';
import DateField from '../../../../common/fields/DateField';
import FunctionField from '../../../../common/fields/FunctionField';

import { SurfaceField } from '../../../surfaces/SurfaceField';

const BG_SHADE = 50;
const ICON_SHADE = 500;

const useStyles = makeStyles((theme) => {
  const baseIcon = {
    marginLeft: 'auto',
  };

  const baseCard = {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    '& .MuiSvgIcon-root': {
      ...baseIcon,
      color: 'inherit',
    },
  };

  return {
    gridItemFlex: {
      display: 'flex',
    },
    scrollTable: {
      overflowX: 'auto',
      maxWidth: '100%',
      minWidth: 0,
      whiteSpace: 'nowrap',
    },
    number: {
      fontSize: '2.25rem',
      fontWeight: 500,
    },
    totalCard: {
      ...baseCard,
      backgroundColor: theme.palette.grey[BG_SHADE],
      '& .MuiSvgIcon-root': {
        ...baseIcon,
        color: theme.palette.grey[ICON_SHADE],
      },
    },
    successCard: {
      ...baseCard,
      backgroundColor: theme.palette.success[BG_SHADE],
      '& .MuiSvgIcon-root': {
        ...baseIcon,
        color: theme.palette.success[ICON_SHADE],
      },
    },
    failedCard: {
      ...baseCard,
      backgroundColor: theme.palette.error[BG_SHADE],
      '& .MuiSvgIcon-root': {
        ...baseIcon,
        color: theme.palette.error[ICON_SHADE],
      },
    },
  };
});

const SlotsCard = ({ cardClass, title, description, icon: Icon, number }) => {
  const translate = useTranslate();
  const classes = useStyles();

  return <Paper variant="outlined" elevation={0} className={cardClass}>
    <Typography className={classes.number}>{number}</Typography>
    <div>
      <Typography>{translate(title)}</Typography>
      <Typography variant="body2" color="textSecondary">{translate(description)}</Typography>
    </div>
    <Icon />
  </Paper >
}

function ResultsStep() {
  const { values: { slots, recurrenceDates, recurrenceDatesValid } } = useFormState();
  const translate = useTranslate();
  const classes = useStyles();

  const slotsWithDates = useMemo(() => {
    if (!recurrenceDates?.length) return [];

    return Object.keys(recurrenceDatesValid)
      .filter(date => recurrenceDates.includes(date))
      .flatMap(date => {
        const dateErrors = recurrenceDatesValid[date]?.errors || [];
        return slots
          .map(slot => {
            const slotError = dateErrors.find(err => err.slotId === slot.id);
            return {
              ...slot,
              id: `${date}-${slot.id}`,
              date,
              reason: slotError?.reason
            };
          })
      });
  }, [recurrenceDates, slots, recurrenceDatesValid]);

  const invalidSlotsContext = useList({
    resource: 'arenaslots',
    data: slotsWithDates || [],
    loading: false,
    loaded: true,
    page: 1,
    perPage: 15,
    filter: { reason: { neq: null } },
    sort: { field: 'date', order: 'ASC' },
  });

  const totalSlots = slotsWithDates.length;
  const validSlots = slotsWithDates.filter(slot => !slot.reason).length;
  const invalidSlots = totalSlots - validSlots;

  return <Grid container spacing={2}>
    <Grid className={classes.gridItemFlex} item xs={12} sm={6} md={4}>
      <SlotsCard
        cardClass={classes.totalCard}
        title="resources.arenaslots.labels.total_slots"
        description="resources.arenaslots.messages.total_slots_description"
        icon={InfoOutlined}
        number={totalSlots}
      />
    </Grid>

    <Grid className={classes.gridItemFlex} item xs={12} sm={6} md={4}>
      <SlotsCard
        cardClass={classes.successCard}
        title="resources.arenaslots.labels.valid_slots"
        description="resources.arenaslots.messages.valid_slots_description"
        icon={CheckCircleOutline}
        number={validSlots}
      />
    </Grid>

    <Grid className={classes.gridItemFlex} item xs={12} md={4}>
      <SlotsCard
        cardClass={classes.failedCard}
        title="resources.arenaslots.labels.invalid_slots"
        description="resources.arenaslots.messages.invalid_slots_description"
        icon={ErrorOutline}
        number={invalidSlots}
      />
    </Grid>

    <Grid item xs={12}>
      <ListContextProvider value={invalidSlotsContext}>
        <Typography variant="h6" gutterBottom>{translate("resources.arenaslots.labels.invalid_slots")}</Typography>
        <Paper className={classes.scrollTable} variant="outlined" elevation={0}>
          <ListView open noResultsText="resources.arenaslots.messages.no_invalid_slots" rowsPerPageOptions={false}>
            <Datagrid>
              <TextField source="eventType" />
              <DateField source="date" />
              <TimeRangeField startSource="startTime" endSource="endTime" />
              <TextField source="duration" />
              <SurfaceField source="surfaceId" cellClassName={classes.truncate} />
              <TextField source="name" cellClassName={classes.truncate} />
              <FunctionField
                source="reason"
                cellClassName={classes.truncate}
                render={({ reason }) => reason && translate(`resources.arenaslots.validations.${reason}`)}
              />
            </Datagrid>
          </ListView>
        </Paper>
      </ListContextProvider>
    </Grid>
  </Grid>
}

export default ResultsStep
